import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Input,
    Textarea
} from '@chakra-ui/react';
import { TransportRequestDetail, TransportRequestTag } from 'data/schemas/TransportRequest';
import { useEffect, useState } from 'react';
import CardTransportRequestEdit from './CardTransportRequestEdit';
import Swal from 'sweetalert2';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    transportRequest: TransportRequestTag;
    handleEditOrUpdate: (body: TransportRequestTag) => Promise<boolean>;
}

function ModalEditTransportRequest(props: Props) {
    const { isOpen, onClose, transportRequest, handleEditOrUpdate } = props;

    const [transportRequestEdit, setTransportRequestEdit] = useState<TransportRequestTag>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setTransportRequestEdit(transportRequest);
    }, [transportRequest])

    const resetAndClose = () => {
        setTransportRequestEdit(transportRequest);
        onClose();
    };

    const handleCloseModal = async () => {
        if (isSubmitting) return;
        if (JSON.stringify(transportRequestEdit) !== JSON.stringify(transportRequest)) {
            const result = await Swal.fire({
                title: 'Discard your changes?',
                text: 'The changes made in this form will be lost.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Discard'
            });
            if (!result.isConfirmed) return;
        }
        resetAndClose();
    };

    const handleSave = async () => {
        if (!transportRequestEdit) return;
        setIsSubmitting(true);
        try {
            if (await handleEditOrUpdate(transportRequestEdit)) resetAndClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTransportRequestEditChange = (
        key: keyof TransportRequestTag,
        value: TransportRequestTag[keyof TransportRequestTag]
    ) => {
        setTransportRequestEdit(prevState => {
            if (!prevState) return prevState;
            return {
                ...prevState,
                [key]: value
            };
        });
    };

    const handleTransportDetailChange = (
        index: number,
        key: keyof TransportRequestDetail,
        value: TransportRequestDetail[keyof TransportRequestDetail]
    ) => {
        setTransportRequestEdit(prevState => {
            if (!prevState) return prevState;

            const updatedDetails = prevState.transportRequestDetail.map((detail, i) =>
                i === index ? { ...detail, [key]: value } : detail
            );

            return {
                ...prevState,
                transportRequestDetail: updatedDetails
            };
        });
    };

    return (
        <Modal
            size="6xl"
            isOpen={isOpen}
            onClose={handleCloseModal}
            closeOnOverlayClick={false}
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent className="h-screen">
                <ModalHeader className="border-b-2">Edit TransportRequest</ModalHeader>
                <ModalCloseButton
                    isDisabled={isSubmitting}
                    className="border hover:bg-red-300"
                />
                <ModalBody>
                    {transportRequestEdit && (
                        <div className="flex h-full flex-row">
                            <div className="flex h-full w-2/4 flex-1 flex-col gap-y-4 pr-2">
                                <div className="flex flex-col gap-y-2 rounded-md border border-neutral-400 p-2">
                                    <div
                                        id="row-1"
                                        className="grid grid-cols-2 gap-x-2"
                                    >
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">
                                                    UAT Tag
                                                </span>
                                                <Input
                                                    name="UAT TAG"
                                                    size="sm"
                                                    className="rounded-sm border-neutral-400 text-black"
                                                    value={transportRequestEdit.uatTag}
                                                    errorBorderColor="crimson"
                                                    disabled
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">
                                                    Detail
                                                </span>
                                                <Textarea
                                                    name="detail"
                                                    size="sm"
                                                    className="flex-1 rounded-sm border-neutral-400 text-black"
                                                    placeholder="กรุณาระบุรายละเอียดงาน"
                                                    errorBorderColor="crimson"
                                                    onChange={(e) => handleTransportRequestEditChange('details', e.target.value)}
                                                    value={transportRequestEdit.details}
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">Remark</span>
                                                <Textarea
                                                    name="remark"
                                                    size="sm"
                                                    className="flex-1 rounded-sm border-neutral-400 text-black"
                                                    placeholder="หมายเหตุเพิ่มเติม"
                                                    onChange={(e) => handleTransportRequestEditChange('remark', e.target.value)}
                                                    value={transportRequestEdit.remark}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            {transportRequestEdit.transportRequestDetail.map((value, index) => (
                                                <CardTransportRequestEdit
                                                    key={index}
                                                    index={index}
                                                    fileUpload={value}
                                                    handleTransportDetailChange={handleTransportDetailChange}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        colorScheme="green"
                        isLoading={isSubmitting}
                        loadingText="Saving"
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default ModalEditTransportRequest
